import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Building2,
  LayoutDashboard,
  ClipboardCheck,
  FolderOpen,
  BookOpen,
  Target,
  ArrowLeft,
  ChevronDown,
  LogOut,
} from "lucide-react";
import { NORMS } from "./norms/normConfig";
import type { User } from "../../types/user";
import logoNew from "../../Assets/logo_new.jpeg";
import "./AlignmentSidebar.css";

interface Props {
  collapsed: boolean;
  onToggle: () => void;
  selectedNorm: string;
  onSelectNorm: (key: string) => void;
  user: User;
  onLogout: () => void;
}

const menuItems = [
  { to: "/alignment/context",    label: "Contexte entreprise", icon: Building2,       color: "#5b3fa6" },
  { to: "/alignment/dashboard",  label: "Dashboard",           icon: LayoutDashboard, color: "#3b82f6" },
  { to: "/alignment/evaluation", label: "Évaluation",          icon: ClipboardCheck,  color: "#059669" },
  { to: "/alignment/dossier",    label: "Dossier d'audit",     icon: FolderOpen,      color: "#d97706" },
];

const toolItems = [
  { to: "/alignment/referentiel", label: "Référentiel",             icon: BookOpen, color: "#b91c1c" },
  { to: "/alignment/objectif",    label: "Objectif de la solution", icon: Target,   color: "#c700ac" },
];

const AlignmentSidebar: React.FC<Props> = ({
  collapsed,
  onToggle,
  selectedNorm,
  onSelectNorm,
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [normOpen, setNormOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const currentNorm = NORMS.find((n) => n.key === selectedNorm) ?? NORMS[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node))
        setNormOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogoutClick = () => {
    try { onLogout(); } catch (_) {}
    window.location.replace(`${process.env.PUBLIC_URL ?? ""}/login`);
  };

  return (
    <aside className={`asm-sidebar${collapsed ? "" : " asm-sidebar--open"}`}>

      {/* ── Header ── */}
      <div className="asm-header">
        <button className="asm-menu-btn" onClick={onToggle} aria-label="Toggle sidebar">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="asm-brand">
          <img src={logoNew} alt="Yameera" className="asm-brand-logo" />
          <span className="asm-brand-name">Yameera</span>
        </div>
      </div>

      <div className="asm-divider" />

      {/* ── Norm selector — at the top ── */}
      <div className="asm-section-label">RÉFÉRENTIEL</div>
      <div className="asm-norm-wrap" ref={dropRef}>
        <button
          className="asm-norm-btn"
          onClick={() => setNormOpen((o) => !o)}
          title={collapsed ? currentNorm.fullName : undefined}
        >
          <img src={currentNorm.img} alt={currentNorm.label} className="asm-norm-img" />
          <span className="asm-norm-text">
            {currentNorm.fullName.length > 26
              ? currentNorm.fullName.slice(0, 26) + "…"
              : currentNorm.fullName}
          </span>
          <ChevronDown size={14} className={`asm-norm-chevron${normOpen ? " asm-norm-chevron--open" : ""}`} />
        </button>

        {normOpen && (
          <div className="asm-norm-dropdown">
            {NORMS.map((n) => (
              <button
                key={n.key}
                className={`asm-norm-option${n.key === selectedNorm ? " asm-norm-option--active" : ""}`}
                onClick={() => { onSelectNorm(n.key); setNormOpen(false); }}
              >
                <img src={n.img} alt={n.label} className="asm-norm-option-img" />
                <div className="asm-norm-option-text">
                  <span className="asm-norm-option-label">{n.label}</span>
                  <span className="asm-norm-option-full">{n.fullName}</span>
                </div>
                {!n.implemented && <span className="asm-wip-badge">Bientôt</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="asm-divider" />

      {/* ── Main navigation ── */}
      <div className="asm-section-label">MENU PROJET</div>
      {menuItems.map(({ to, label, icon: Icon, color }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) => `asm-nav-item${isActive ? " asm-nav-item--active" : ""}`}
          style={({ isActive }) => isActive ? { backgroundColor: color + "15", color } : undefined}
          title={collapsed ? label : undefined}
        >
          <Icon size={22} className="asm-nav-icon" style={{ color, flexShrink: 0 }} />
          <span className="asm-nav-label">{label}</span>
        </NavLink>
      ))}

      <div className="asm-divider" />

      {/* ── Tool items ── */}
      <div className="asm-section-label">OUTILS</div>
      {toolItems.map(({ to, label, icon: Icon, color }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) => `asm-nav-item${isActive ? " asm-nav-item--active" : ""}`}
          style={({ isActive }) => isActive ? { backgroundColor: color + "15", color } : undefined}
          title={collapsed ? label : undefined}
        >
          <Icon size={22} className="asm-nav-icon" style={{ color, flexShrink: 0 }} />
          <span className="asm-nav-label">{label}</span>
        </NavLink>
      ))}

      <div style={{ flex: 1 }} />
      <div className="asm-divider" />

      {/* ── Back to platform ── */}
      <button
        className="asm-nav-item asm-back-btn"
        onClick={() => navigate("/dashboard")}
        title={collapsed ? "Retour à la plateforme" : undefined}
      >
        <ArrowLeft size={22} className="asm-nav-icon" style={{ color: "#374151", flexShrink: 0 }} />
        <span className="asm-nav-label">Retour à la plateforme</span>
      </button>

      {/* ── Logout ── */}
      <button
        className="asm-nav-item"
        onClick={handleLogoutClick}
        title={collapsed ? "Déconnexion" : undefined}
      >
        <LogOut size={22} className="asm-nav-icon" style={{ color: "#ef4444", flexShrink: 0 }} />
        <span className="asm-nav-label" style={{ color: "#ef4444" }}>Déconnexion</span>
      </button>

    </aside>
  );
};

export default AlignmentSidebar;
